import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { SSAOPass } from 'three/examples/jsm/postprocessing/SSAOPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { ColorCorrectionShader } from 'three/examples/jsm/shaders/ColorCorrectionShader.js';
import { VignetteShader } from 'three/examples/jsm/shaders/VignetteShader.js';
import {
  BLOOM_RADIUS, BLOOM_STRENGTH, BLOOM_STRENGTH_LOW_TIER, BLOOM_THRESHOLD,
  GRADE_POW, TONE_MAPPING_EXPOSURE, VIGNETTE_DARKNESS, VIGNETTE_OFFSET,
} from '../config/lighting-tuning';

/**
 * Game Feel Polish Phase 4 — EffectComposer stack: SSAO, bloom, ACES tone
 * mapping, a color grade, and a vignette. No real color-grading LUT file
 * exists anywhere in this repo (same "zero asset files" situation Phase
 * 3's audio hit — see `config/audio-tuning.ts`'s file comment) — three's
 * own `ColorCorrectionShader` (lift/gamma/gain via addRGB/powRGB/mulRGB)
 * substitutes for a LUT-based grade rather than hand-rolling a custom
 * shader for something three.js already ships. It's a single global
 * formula (`mulRGB * pow(color + addRGB, powRGB)`), not a true 3-way
 * shadow/midtone/highlight corrector, so the tuning below is deliberately
 * a light, uniform tint rather than an attempted highlight/shadow split.
 *
 * Since `EffectComposer.render()` replaces the direct `renderer.render()`
 * call, `OutputPass` MUST be the final pass — modern three.js post-
 * processing passes work in linear space, and OutputPass is what applies
 * `renderer.toneMapping`/`renderer.outputColorSpace` on the way out
 * (skipping it would leave the image looking flat/washed out even with
 * ACES tone mapping set on the renderer).
 *
 * SSAO is skipped on coarse-pointer (touch/mobile) or low-core-count
 * devices — CLAUDE.md's 60fps-on-a-mid-range-2022-Android-phone target,
 * and SSAO is by far the most expensive pass here (its own depth+normal
 * render plus a blur pass). Bloom/grading/vignette/tone-mapping are cheap
 * enough to run everywhere, just at a slightly cheaper bloom setting.
 */
/**
 * SSAO at half resolution with half the kernel (2026-10-15, QA #9: "GPU at
 * 94%"). It was the most expensive thing in the frame on desktop: a second
 * full-scene render (normals + depth) plus a 32-tap kernel per pixel, all at
 * the full physical resolution. Ambient occlusion is a soft, blurred term by
 * nature, so a half-size buffer upscaled through the pass's own blur reads the
 * same in the corners it darkens, and 16 taps over a 0.6m radius is still
 * smooth after that blur — compared side by side in headless screenshots
 * before shipping. Roughly a quarter of the fragment work it used to cost.
 */
const SSAO_RESOLUTION_SCALE = 0.5;
const SSAO_KERNEL_SIZE = 16;

class ScaledSSAOPass extends SSAOPass {
  override setSize(width: number, height: number): void {
    super.setSize(Math.max(1, Math.round(width * SSAO_RESOLUTION_SCALE)), Math.max(1, Math.round(height * SSAO_RESOLUTION_SCALE)));
  }
}

const IS_LOW_TIER = typeof window !== 'undefined' && typeof navigator !== 'undefined'
  && (window.matchMedia?.('(pointer: coarse)').matches || (navigator.hardwareConcurrency ?? 8) <= 4);

export class PostProcessingStack {
  private readonly composer: EffectComposer;

  constructor(renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.PerspectiveCamera) {
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    // Post-Phase-4 playability fix — see dungeon-instance-scene.ts's
    // buildLighting() comment for the full reasoning (lighting.md Core Rule
    // #4: light has no observer-side effect, so a dim render enforces nothing).
    // Kept moderate and paired with a raised bloom threshold below: exposure
    // pushes the whole midtone range toward that threshold, and overshooting
    // it is what washed the frame out during Phase 4's own tuning.
    renderer.toneMappingExposure = TONE_MAPPING_EXPOSURE;

    this.composer = new EffectComposer(renderer);
    this.composer.addPass(new RenderPass(scene, camera));

    if (!IS_LOW_TIER) {
      const ssao = new ScaledSSAOPass(scene, camera, window.innerWidth, window.innerHeight, SSAO_KERNEL_SIZE);
      ssao.kernelRadius = 0.6; // tuned in world-meters, not the three.js demo's default unit scale — Tallow's SHOULDER_HEIGHT_M etc. are real meters
      ssao.minDistance = 0.0005;
      ssao.maxDistance = 0.15;
      this.composer.addPass(ssao);
    }

    const bloom = new UnrealBloomPass(
      new THREE.Vector2(window.innerWidth, window.innerHeight),
      IS_LOW_TIER ? BLOOM_STRENGTH_LOW_TIER : BLOOM_STRENGTH, // strength
      BLOOM_RADIUS, // radius
      BLOOM_THRESHOLD, // threshold — only braziers/lanterns/ability VFX/emissive bits bloom, not general lit geometry (raised with exposure, so brightening didn't drag ordinary walls into the bloom pass)
    );
    this.composer.addPass(bloom);

    const grade = new ShaderPass(ColorCorrectionShader);
    (grade.uniforms.mulRGB.value as THREE.Vector3).set(1.02, 1.0, 1.04); // faint cool-purple lift
    (grade.uniforms.addRGB.value as THREE.Vector3).set(0, 0, 0.006);
    /**
     * **Set explicitly, and this is the fix for "the dungeon is too dark".**
     *
     * `ColorCorrectionShader` computes `mulRGB * pow(texel + addRGB, powRGB)`,
     * and its `powRGB` uniform **defaults to (2, 2, 2)**. This pass has always
     * set `mulRGB` and `addRGB` and never touched `powRGB`, so every pixel in
     * the frame was being *squared* — in linear space, before tone mapping.
     * A surface sitting at 0.28 came out at 0.078.
     *
     * That was the dominant cause, and it explains why the previous
     * brightening pass (which raised ambient and exposure) barely helped:
     * every unit of light added was squared away again. Measured on the same
     * five-frame walk, neutralising it moved the median luminance of the
     * central frame from 61 to 162 and the fraction below luma 25 from 34% to
     * zero — a bigger swing than every light change in this commit combined.
     *
     * Left slightly above 1.0 rather than exactly neutral: a gentle curve
     * deepens shadows and suits a dungeon. It is a deliberate value now, not
     * an inherited default nobody noticed.
     */
    (grade.uniforms.powRGB.value as THREE.Vector3).set(GRADE_POW, GRADE_POW, GRADE_POW);
    this.composer.addPass(grade);

    // VignetteShader mixes each pixel toward vec3(1.0 - darkness) as it
    // nears the edge (strength of the mix set by `offset`) — darkness must
    // stay close to 1.0 for edges to read as genuinely dark; dropping it
    // shifts the target color toward grey instead of softening the effect
    // (confirmed live: darkness=0.7 produced a flat grey wash, not a
    // gentler vignette). `offset` controls how far the effect reaches, NOT
    // how strong it is — lower offset keeps more of the frame's center clear.
    // `offset` pulled in from 0.9 as part of the same playability fix — it
    // controls how far the darkening reaches toward the centre, and at 0.9 it
    // was eating enough of the frame to hide geometry the player needs.
    const vignette = new ShaderPass(VignetteShader);
    vignette.uniforms.offset.value = VIGNETTE_OFFSET;
    vignette.uniforms.darkness.value = VIGNETTE_DARKNESS;
    this.composer.addPass(vignette);

    this.composer.addPass(new OutputPass());
  }

  render(): void {
    this.composer.render();
  }

  /** The composer caches the pixel ratio it was built with; adaptive resolution changes it at runtime. */
  setPixelRatio(ratio: number): void {
    this.composer.setPixelRatio(ratio);
  }

  /** `EffectComposer.setSize` already resizes every pass's own render targets internally — no per-pass resize calls needed here. */
  setSize(width: number, height: number): void {
    this.composer.setSize(width, height);
  }

  dispose(): void {
    this.composer.dispose();
  }
}
